import {
  Box,
  Group,
  List,
  LoadingOverlay,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core"
import {
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconCircleCheck,
  IconCircleX,
} from "@tabler/icons-react"
import { Card, SegmentedBadge } from "@tfg/ui"
import { useTranslation } from "react-i18next"
import type { ReactNode } from "react"

import { CustomDrawer } from "~/components/CustomDrawer.tsx"
import {
  ComplianceDetailsDto,
  FindProductsProductStandardDto,
  findProductsProductStandardCertificateDtoCertificateRequestStatusEnum,
  findProductsProductStandardCertificateDtoStatusEnum,
  useFindProductStandardCertificates,
} from "~/gen/api"
import { CertificateSummary } from "~/pages/Brand/Certificates/components/CertificateSummary.tsx"
import { standardRulesTranslations } from "~/pages/Brand/Certificates/type.ts"

const STANDARD_DRAWER_STATUS_ICON_PX = 16 as const

function FixedCircleDrawerIcon({ children }: { children: ReactNode }) {
  return (
    <Box
      component="span"
      w={STANDARD_DRAWER_STATUS_ICON_PX}
      h={STANDARD_DRAWER_STATUS_ICON_PX}
      miw={STANDARD_DRAWER_STATUS_ICON_PX}
      mih={STANDARD_DRAWER_STATUS_ICON_PX}
      style={{
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {children}
    </Box>
  )
}

type Props = {
  standard: FindProductsProductStandardDto
  productId: string
  onClose: () => void
}

export const StandardComplianceDrawer = ({
  standard,
  productId,
  onClose,
}: Props) => {
  return (
    <CustomDrawer size="lg" onClose={onClose} opened={true}>
      <Group
        mih="100%"
        bg="var(--mantine-color-brand-background)"
        align="start"
      >
        <Stack w="100%" p="xl" justify="start" align="start">
          <Title order={2}>{standard.name}</Title>
          <ComplianceStandardView standard={standard} productId={productId} />
        </Stack>
      </Group>
    </CustomDrawer>
  )
}

const ruleListThemeIconStyle = { flexShrink: 0 } as const
const ruleListInnerCircleIconStyle = {
  width: STANDARD_DRAWER_STATUS_ICON_PX,
  height: STANDARD_DRAWER_STATUS_ICON_PX,
  flexShrink: 0,
} as const

const CompliantIcon = () => (
  <ThemeIcon
    color="primary"
    size={24}
    radius="xl"
    style={ruleListThemeIconStyle}
  >
    <IconCircleCheck style={ruleListInnerCircleIconStyle} />
  </ThemeIcon>
)

const NotCompliantIcon = () => (
  <ThemeIcon
    color="var(--mantine-color-error)"
    size={24}
    radius="xl"
    style={ruleListThemeIconStyle}
  >
    <IconCircleX style={ruleListInnerCircleIconStyle} />
  </ThemeIcon>
)

const ComplianceStandardView = ({
  standard,
  productId,
}: {
  standard: FindProductsProductStandardDto
  productId: string
}) => {
  const { t } = useTranslation("certificates")

  const { data: certificatesData, isLoading: isLoadingCertificates } =
    useFindProductStandardCertificates(
      {
        productId,
        standardId: standard.id,
      },
      {
        query: {
          refetchOnWindowFocus: "always",
        },
      }
    )

  if (!certificatesData || isLoadingCertificates) {
    return <LoadingOverlay visible={true} overlayProps={{ blur: 2 }} />
  }

  const hasMissingCertificates =
    certificatesData.data.processed < certificatesData.data.total
  const validatedCertificates = certificatesData.data.certificates.filter(
    (certificate) =>
      certificate.certificateRequestStatus ===
        findProductsProductStandardCertificateDtoCertificateRequestStatusEnum.ACCEPTED &&
      certificate.status ===
        findProductsProductStandardCertificateDtoStatusEnum.PROCESSED
  ).length
  const pendingReviewCertificates = certificatesData.data.certificates.filter(
    (certificate) =>
      certificate.certificateRequestStatus ===
        findProductsProductStandardCertificateDtoCertificateRequestStatusEnum.SUBMITTED &&
      (certificate.status ===
        findProductsProductStandardCertificateDtoStatusEnum.PENDING ||
        certificate.status ===
          findProductsProductStandardCertificateDtoStatusEnum.PROCESSED)
  ).length
  const notUploadedCertificates = certificatesData.data.certificates.filter(
    (certificate) =>
      certificate.certificateRequestStatus ===
      findProductsProductStandardCertificateDtoCertificateRequestStatusEnum.OPENED
  ).length
  const validatedTooltip = `${t(
    "certificateRequestStatus.ACCEPTED"
  )}: ${validatedCertificates}. ${t(
    "standard.drawer.segmentBadgeTooltipValidated",
    {
      count: validatedCertificates,
    }
  )}`
  const pendingReviewTooltip = `${t(
    "certificateRequestStatus.SUBMITTED"
  )}: ${pendingReviewCertificates}. ${t(
    "standard.drawer.segmentBadgeTooltipPendingReview",
    {
      count: pendingReviewCertificates,
    }
  )}`
  const notUploadedTooltip = `${t(
    "certificateStatus.NOT_UPLOADED"
  )}: ${notUploadedCertificates}. ${t(
    "standard.drawer.segmentBadgeTooltipNotUploaded",
    {
      count: notUploadedCertificates,
    }
  )}`

  const showComplianceRules =
    !hasMissingCertificates && standard.complianceDetails.details.length > 0
  const totalComplianceRules = standard.complianceDetails.details.length
  const compliantRules = standard.complianceDetails.details.filter(
    (rule) => rule.isCompliant
  ).length
  const hasUncompliantRules =
    !hasMissingCertificates &&
    standard.complianceDetails.details.some((rule) => !rule.isCompliant)

  return (
    <Stack>
      <Stack gap="xs">
        <Group gap="xs" wrap="nowrap" align="center">
          {hasMissingCertificates ? (
            <FixedCircleDrawerIcon>
              <IconCircleX
                size={STANDARD_DRAWER_STATUS_ICON_PX}
                color="var(--mantine-color-error)"
              />
            </FixedCircleDrawerIcon>
          ) : (
            <FixedCircleDrawerIcon>
              <IconCircleCheck
                size={STANDARD_DRAWER_STATUS_ICON_PX}
                color="var(--mantine-color-success)"
              />
            </FixedCircleDrawerIcon>
          )}
          <Text fz="sm" fw={500}>
            {t("standard.drawer.certificatesNumber", {
              count: certificatesData.data.total,
              completedCertificates: certificatesData.data.processed,
              totalCertificates: certificatesData.data.total,
            })}
          </Text>
        </Group>
        {showComplianceRules ? (
          <Group gap="xs" wrap="nowrap" align="start">
            {hasUncompliantRules ? (
              <FixedCircleDrawerIcon>
                <IconCircleX
                  size={STANDARD_DRAWER_STATUS_ICON_PX}
                  color="var(--mantine-color-error)"
                />
              </FixedCircleDrawerIcon>
            ) : (
              <FixedCircleDrawerIcon>
                <IconCircleCheck
                  size={STANDARD_DRAWER_STATUS_ICON_PX}
                  color="var(--mantine-color-success)"
                />
              </FixedCircleDrawerIcon>
            )}
            <Text fw={500} fz="sm">
              {hasUncompliantRules
                ? t("standard.drawer.complianceRulesPartial", {
                    compliantRules,
                    totalRules: totalComplianceRules,
                  })
                : t("standard.drawer.complianceRulesAllCompliant", {
                    totalRules: totalComplianceRules,
                  })}
            </Text>
          </Group>
        ) : null}
      </Stack>
      <SegmentedBadge
        w="fit-content"
        items={[
          <Tooltip key="validated" label={validatedTooltip} multiline maw={320}>
            <Group gap="var(--mantine-spacing-xxs)" wrap="nowrap">
              <IconCheck size={12} color="green" />
              <Text fz="xs">
                {t("certificateRequestStatus.ACCEPTED")}:{" "}
                {validatedCertificates}
              </Text>
            </Group>
          </Tooltip>,
          <Tooltip
            key="pending-review"
            label={pendingReviewTooltip}
            multiline
            maw={320}
          >
            <Group gap="var(--mantine-spacing-xxs)" wrap="nowrap">
              <IconAlertTriangle
                size={12}
                color="var(--mantine-color-yellow-7)"
              />
              <Text fz="xs">
                {t("certificateRequestStatus.SUBMITTED")}:{" "}
                {pendingReviewCertificates}
              </Text>
            </Group>
          </Tooltip>,
          <Tooltip
            key="not-uploaded"
            label={notUploadedTooltip}
            multiline
            maw={320}
          >
            <Group gap="var(--mantine-spacing-xxs)" wrap="nowrap">
              <IconClock size={12} color="var(--mantine-color-gray-6)" />
              <Text fz="xs">
                {t("certificateStatus.NOT_UPLOADED")}: {notUploadedCertificates}
              </Text>
            </Group>
          </Tooltip>,
        ]}
      />
      <Stack w="100%" gap="var(--tfg-molecule-padding-y)">
        {certificatesData.data.certificates.map((certificate, index) => (
          <Stack
            bg="var(--mantine-color-brand-background)"
            h="100%"
            w="100%"
            gap="var(--tfg-organism-padding-y)"
            justify="start"
            align="start"
            key={index}
          >
            <Card style={{ width: "100%" }}>
              <Stack>
                <CertificateSummary
                  showDocumentIcons={true}
                  certificate={certificate}
                  maxListElements={5}
                />
              </Stack>
            </Card>
          </Stack>
        ))}
      </Stack>
      {showComplianceRules ? (
        <Stack>
          <Title order={4}>{t("standard.drawer.complianceRules")}</Title>
          <List spacing="lg" size="md" center pl={0}>
            {standard.complianceDetails.details.map(
              (rule: ComplianceDetailsDto, index) => {
                const { icon, description } = rule.isCompliant
                  ? {
                      icon: <CompliantIcon />,
                      description:
                        standardRulesTranslations[rule.description].success,
                    }
                  : {
                      icon: <NotCompliantIcon />,
                      description:
                        standardRulesTranslations[rule.description].failure,
                    }

                return (
                  <Tooltip
                    key={index}
                    color="white"
                    c="black"
                    position="top-start"
                    offset={5}
                    label={t(description)}
                    multiline
                    maw={400}
                  >
                    <List.Item icon={icon} ml={0}>
                      {t(standardRulesTranslations[rule.name])}
                    </List.Item>
                  </Tooltip>
                )
              }
            )}
          </List>
        </Stack>
      ) : null}
    </Stack>
  )
}
